import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import GetUserInfoService from "../../../../../../service/GetUserInfoService";
import localeText from "../../../../../../locale";
import { useToastContext } from "../../../../../../contexts/ToastContext/ToastContext";
import PasswordStrengthIndicator from "./PasswordStrengthIndicator";
import { Password } from "primereact/password";
import "./ChangePasswordSection.css";

const getUserInfoService = new GetUserInfoService();

const ChangePasswordSection = () => {
  const { showSuccessToast, showErrorToast } = useToastContext();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasLength, setHasLength] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setHasUppercase(newPassword?.match(/[A-Z]/) ? true : false);
    setHasNumber(newPassword?.match(/\d/) ? true : false);
    setHasLength(newPassword?.length >= 8 ? true : false);
  }, [newPassword]);

  async function handleSubmit() {
    if (newPassword !== confirmPassword) {
      showErrorToast({ detail: "passwordFieldsMismatched" });
      return;
    }
    setLoading(true);
    try {
      const res = await getUserInfoService.postChangePassword(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        currentPassword,
        newPassword,
      );
      if (res.messageCode !== 200000) {
        const msg = localeText("data")?.find((m) => m.code === res.messageCode)?.message;
        showErrorToast({ detail: msg || "error" });
      } else {
        showSuccessToast({ detail: "passwordChangedSuccessfully" });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      } else {
        showErrorToast({ detail: "unexpectedSystemBehaviour" });
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h2 className="text-lg font-semibold m-0 mb-1" style={{ color: "var(--theme-text)" }}>
        {localeText("changePassword")}
      </h2>
      <p className="text-sm m-0 mb-3" style={{ color: "var(--theme-text-muted)" }}>
        {localeText("changePasswordSubtitle")}
      </p>
      <hr className="mb-3" />
      <div className="flex flex-column gap-3">
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("currentPassword")} *
          </label>
          <div className="password-with-lock-wrapper">
            <i className="pi pi-lock password-with-lock-icon" />
            <Password
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              inputClassName="w-full p-no-outline border-round-md password-with-lock-input"
              className="w-full"
              style={{ color: "var(--theme-text)" }}
              toggleMask
              feedback={false}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("newPassword")} *
          </label>
          <div className="password-with-lock-wrapper">
            <i className="pi pi-lock password-with-lock-icon" />
            <Password
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              inputClassName="w-full p-no-outline border-round-md password-with-lock-input"
              className="w-full"
              style={{ color: "var(--theme-text)" }}
              toggleMask
              feedback={false}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("confirmPassword")} *
          </label>
          <div className="password-with-lock-wrapper">
            <i className="pi pi-lock password-with-lock-icon" />
            <Password
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              inputClassName="w-full p-no-outline border-round-md password-with-lock-input"
              className="w-full"
              style={{ color: "var(--theme-text)" }}
              toggleMask
              feedback={false}
            />
          </div>
          <div className="mt-3 mb-2">
            <PasswordStrengthIndicator password={newPassword} />
          </div>
          <div>
            <span className="text-xs font-medium" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("passwordMustContainAtLeast")}
            </span>
            <ul className="list-none mt-2 p-0 flex flex-column gap-2">
              <li className="flex align-items-center gap-2">
                {hasUppercase ? (
                  <div className="password-restriction-icon-check-container border-circle flex align-items-center justify-content-center">
                    <i className="pi pi-check font-bold password-restriction-icon-check" />
                  </div>
                ) : (
                  <div className="password-restriction-icon-times-container border-circle flex align-items-center justify-content-center">
                    <i className="pi pi-times font-bold password-restriction-icon-times" />
                  </div>
                )}
                <span className="text-xs">{localeText("passwordRequirementUppercase")}</span>
              </li>
              <li className="flex align-items-center gap-2">
                {hasNumber ? (
                  <div className="password-restriction-icon-check-container border-circle flex align-items-center justify-content-center">
                    <i className="pi pi-check font-bold password-restriction-icon-check" />
                  </div>
                ) : (
                  <div className="password-restriction-icon-times-container border-circle flex align-items-center justify-content-center">
                    <i className="pi pi-times font-bold password-restriction-icon-times" />
                  </div>
                )}
                <span className="text-xs">{localeText("passwordRequirementNumber")}</span>
              </li>
              <li className="flex align-items-center gap-2">
                {hasLength ? (
                  <div className="password-restriction-icon-check-container border-circle flex align-items-center justify-content-center">
                    <i className="pi pi-check font-bold password-restriction-icon-check" />
                  </div>
                ) : (
                  <div className="password-restriction-icon-times-container border-circle flex align-items-center justify-content-center">
                    <i className="pi pi-times font-bold password-restriction-icon-times" />
                  </div>
                )}
                <span className="text-xs">{localeText("passwordRequirementLength")}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="flex gap-2 mt-2" style={{ height: "40px" }}>
          <Button
            label={localeText("cancel")}
            className="p-button-outlined border-round-md flex-1 text-sm font-medium"
            style={{ borderColor: "var(--theme-border)", color: "var(--theme-text-muted)" }}
            onClick={() => {
              setCurrentPassword("");
              setNewPassword("");
              setConfirmPassword("");
            }}
          />
          <Button
            label={localeText("applyChanges") || localeText("confirm")}
            className="border-round-md flex-1 text-sm font-medium"
            loading={loading}
            style={{ backgroundColor: "var(--theme-primary)", color: "#fff", border: "none" }}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordSection;
